import { useCallback, useRef } from 'react'
import { AppWriteContext } from '../Contexts/AppWriteContext'
import glob_const from '../libs/glob_const'

export interface StockResult {
   /** Stock agrégé par SKU sur l'ensemble des dépôts interrogés (clé = SKU, valeur = qté sommée ou 'NC') */
   stocks: Record<string, string | number>
   /** Total par dépôt, prêt pour l'affichage (clé = code dépôt brut ex. "SFR", valeur = qté ou 'NC') */
   per_depot: Record<string, string | number>
   /** libellé prêt à afficher — ex: "FR: 12, US: 7, CN: NC, JP: 23" */
   label: string
   /** somme numérique (null si aucune valeur numérique) */
   total: number | null
   /** true si au moins un dépôt/SKU a du stock > 0 */
   available: boolean
   /** true si TOUTES les requêtes ont échoué pour autre raison qu'un 404 (réseau, auth, etc.) :
       on n'a pas pu déterminer le stock — à distinguer de "pas de stock" (404 partout = stock 0). */
   error: boolean
}

function buildLabel(obj: Record<string, string | number>): string {
   return Object.entries(obj)
      .map(([k, v]) => `${k.startsWith('S') ? k.substring(1) : k}: ${v}`)
      .join(', ')
}

function buildTotal(obj: Record<string, string | number>): number | null {
   let hasNum = false
   const sum = Object.values(obj).reduce<number>((acc, v) => {
      const n = typeof v === 'number' ? v : Number(v)
      if (Number.isFinite(n)) { hasNum = true; return acc + n }
      return acc
   }, 0)
   return hasNum ? sum : null
}

function buildAvailable(obj: Record<string, string | number>): boolean {
   return Object.values(obj).some(v => {
      const n = typeof v === 'number' ? v : Number(v)
      return Number.isFinite(n) && n > 0
   })
}

/** Parse le `sku_stocks` renvoyé par AW pour un dépôt en map { sku → qty | 'NC' }. */
function parseDepotPayload(data: unknown): Record<string, string | number> {
   const raw = typeof data === 'string' ? JSON.parse(data) : (data ?? {})
   return (raw && typeof raw === 'object') ? (raw as Record<string, string | number>) : {}
}

const EMPTY_RESULT: StockResult = { stocks: {}, per_depot: {}, label: '', total: null, available: false, error: false }

/**
 * Factorise l'interrogation des stocks AW.
 * - `depots` peut être :
 *    • `null`            → fallback sur `glob_const.depot_defaut` (legacy bjx)
 *    • `string`          → un seul dépôt
 *    • `string[]`        → tous interrogés en parallèle puis agrégés
 *      - `stocks`        : somme par SKU sur l'ensemble des dépôts
 *      - `per_depot`     : total par dépôt (pour le label détaillé)
 *      - `label`         : "FR: 12, US: 7, CN: NC, …" basé sur per_depot
 * - cache intra-session par (clé dépôts, model) pour éviter les re-fetch.
 * - tableau vide → résultat vide silencieux (cas d'un client sans `depot_retail`).
 */
export default function useStock() {
   const { getStock } = { ...AppWriteContext() } as { getStock: (model: string, depot: string | null) => Promise<{ status: boolean, notFound?: boolean, data: unknown }> }
   const cacheRef = useRef<Map<string, StockResult>>(new Map())

   const fetchStock = useCallback(async (
      model_id: string,
      depots: string | string[] | null = null,
   ): Promise<StockResult> => {
      // Normalisation en liste de dépôts à interroger.
      let depotList: string[]
      if (Array.isArray(depots)) {
         depotList = depots
      } else {
         depotList = [depots ?? glob_const.depot_defaut]
      }

      if (depotList.length === 0) return EMPTY_RESULT

      const cacheKey = `${depotList.join('|')}-${model_id}`
      const cached = cacheRef.current.get(cacheKey)
      if (cached) return cached

      const responses = await Promise.all(depotList.map(d => getStock(model_id, d)))

      const aggregatedStocks: Record<string, number | 'NC'> = {}
      const perDepot: Record<string, number | 'NC'> = {}
      // Compteur d'erreurs réseau/auth (= status:false sans notFound). Si toutes les requêtes
      // sont dans ce cas, on ne peut RIEN dire du stock → on remonte error=true pour que l'UI
      // affiche "Indisponible" (vrai problème) plutôt que "Pas de stock" (zéro confirmé).
      let errorCount = 0

      for (let i = 0; i < depotList.length; i++) {
         const d = depotList[i]
         const data = responses[i]
         if (!data?.status) {
            if (!data?.notFound) errorCount++
            perDepot[d] = 0
            continue
         }
         const obj = parseDepotPayload(data.data)
         let depotTotal = 0
         let depotHasNum = false
         let depotAllNC = true
         for (const [sku, qty] of Object.entries(obj)) {
            const n = typeof qty === 'number' ? qty : Number(qty)
            if (Number.isFinite(n)) {
               depotHasNum = true
               depotAllNC = false
               depotTotal += n
               const cur = aggregatedStocks[sku]
               aggregatedStocks[sku] = (typeof cur === 'number' ? cur : 0) + n
            } else {
               // 'NC' ou non-numérique : on garde 'NC' uniquement si aucun autre dépôt n'a fourni un nombre pour ce SKU
               if (!(sku in aggregatedStocks)) aggregatedStocks[sku] = 'NC'
            }
         }
         perDepot[d] = depotHasNum ? depotTotal : (depotAllNC && Object.keys(obj).length > 0 ? 'NC' : 0)
      }

      const result: StockResult = {
         stocks: aggregatedStocks,
         per_depot: perDepot,
         label: buildLabel(perDepot),
         total: buildTotal(perDepot),
         available: buildAvailable(perDepot),
         error: errorCount === depotList.length,
      }

      cacheRef.current.set(cacheKey, result)
      return result
   }, [getStock])

   const clearCache = useCallback(() => {
      cacheRef.current.clear()
   }, [])

   return { fetchStock, clearCache }
}
